import { ExternalLink } from "lucide-react";
import type { Components } from "react-markdown";
import Markdown from "react-markdown";
import { Link } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { citationLabel, companyHrefOrgnr, isCitationHref } from "./plutoSourceCitations";

/**
 * Pluto answers in markdown: bold for the numbers that matter, bullets for
 * options, and inline links for web sources. Raw HTML is never rendered, since
 * answers can quote untrusted web pages.
 */
const components: Components = {
  p: ({ children }) => <p className="mt-3 text-sm leading-6 text-stone-800 first:mt-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-stone-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mt-3 list-disc space-y-1 pl-5 text-sm leading-6 text-stone-800 first:mt-0">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm leading-6 text-stone-800 first:mt-0">{children}</ol>
  ),
  li: ({ children }) => <li className="marker:text-stone-400">{children}</li>,
  h1: ({ children }) => <h1 className="mt-4 text-sm font-semibold text-stone-900 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-4 text-sm font-semibold text-stone-900 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-4 text-sm font-semibold text-stone-900 first:mt-0">{children}</h3>,
  blockquote: ({ children }) => (
    <blockquote className="mt-3 border-l-2 border-stone-200 pl-3 text-sm leading-6 text-stone-600 first:mt-0">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="rounded bg-stone-100 px-1 py-0.5 font-mono text-[12px] text-stone-800">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="mt-3 overflow-x-auto rounded-lg bg-stone-900 p-3 text-xs leading-5 text-stone-100 first:mt-0 [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit">
      {children}
    </pre>
  ),
  hr: () => <hr className="my-4 border-stone-200" />,
  table: ({ children }) => (
    <div className="mt-3 overflow-x-auto first:mt-0">
      <table className="w-full border-collapse text-left text-xs text-stone-800">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border-b border-stone-200 px-2 py-1 font-semibold text-stone-900">{children}</th>
  ),
  td: ({ children }) => <td className="border-b border-stone-100 px-2 py-1 align-top">{children}</td>,
  a: ({ children, href }) => {
    if (href === undefined) return <span>{children}</span>;

    // A company reads as part of the sentence rather than as a source, so it
    // keeps its name and stays inside the app. A citation is the opposite: it
    // is shown as the site it came from and leaves.
    const orgnr = companyHrefOrgnr(href);
    if (orgnr !== null) {
      return (
        <Link
          className="font-medium text-stone-900 underline decoration-stone-300 underline-offset-2 hover:decoration-stone-500"
          to={`/company/${orgnr}`}
        >
          {children}
        </Link>
      );
    }

    return isCitationHref(href) ? (
      <a
        className="mx-0.5 inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 align-baseline text-[11px] font-medium text-stone-700 hover:bg-stone-200"
        href={href}
        rel="noreferrer noopener"
        target="_blank"
      >
        <ExternalLink aria-hidden="true" className="h-3 w-3" />
        {citationLabel(href)}
      </a>
    ) : (
      <span>{children}</span>
    );
  },
};

const plugins = [remarkGfm, remarkBreaks];

export const PlutoAnswerMarkdown = ({ text }: { text: string }) => (
  <div className="text-sm leading-6 text-stone-800">
    <Markdown components={components} remarkPlugins={plugins}>
      {text}
    </Markdown>
  </div>
);
